/**
 * Care-guide body helpers (2026-10-12, "photos through the body of long
 * guides").
 *
 * ONE reader — `readGuideHeadings` — answers "which lines of this guide
 * are headings that will actually RENDER as headings", and everything
 * else reads it:
 *
 *  - the Table of Contents (its H2s, in plain text);
 *  - the heading ids (each rendered heading looks its id up by the line
 *    it stands on, so a TOC link and its heading cannot be derived two
 *    ways);
 *  - `splitGuideSections` (its H2s are the only split points).
 *
 * A `## ` line inside fenced code, an HTML comment or an HTML block is not
 * a heading — react-markdown renders it as code or text — so it is none
 * of the three.
 */

/** The app's id prefix for user-authored anchors (rehype-sanitize's
 *  default clobber prefix, the rule SitePageBody's bodies follow) — a
 *  heading called "Related Frogs" can never take the page's own
 *  `#related-frogs`. */
export const HEADING_ID_PREFIX = 'user-content-';

/** The slug of a heading's plain text: the rule the TOC has always used. */
export const slugForHeading = (text) => String(text || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9\s-]/g, '')
  .replace(/\s+/g, '-')
  .replace(/-+/g, '-')
  .replace(/^-|-$/g, '');

/** A heading's markdown reduced to the words a reader sees. */
export const plainHeadingText = (raw) => String(raw || '')
  .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
  .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
  .replace(/\[([^\]]*)\]\[[^\]]*\]/g, '$1')
  .replace(/<[^>]+>/g, '')
  .replace(/(`+)([^`]*?)\1/g, '$2')
  .replace(/(\*\*|__)(.+?)\1/g, '$2')
  .replace(/\*(.+?)\*/g, '$1')
  .replace(/(^|[^\w])_(.+?)_(?=[^\w]|$)/g, '$1$2')
  .replace(/~~(.+?)~~/g, '$1')
  .replace(/\\([\\`*_{}[\]()#+\-.!>~|])/g, '$1')
  .replace(/&amp;/g, '&')
  .replace(/&nbsp;/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

// Lines with their own newline. No lookbehind regex: older iOS Safari
// cannot parse one, and a parse error takes the whole chunk down.
const linesOf = (src) => src.match(/[^\n]*\n|[^\n]+$/g) || [];

const FENCE = /^ {0,3}(`{3,}|~{3,})/;
const ATX = /^ {0,3}(#{1,6})(?=[ \t]|$)(.*)$/;
// CommonMark HTML blocks, by the condition that starts and ends each.
const HTML_RAW_OPEN = /^<(script|pre|style|textarea)(?=[\s>]|$)/i;
const HTML_RAW_CLOSE = /<\/(script|pre|style|textarea)>/i;
const HTML_BLOCK_TAGS = 'address|article|aside|base|basefont|blockquote|body|caption|center|col|colgroup|dd|details|dialog|dir|div|dl|dt|fieldset|figcaption|figure|footer|form|frame|frameset|h[1-6]|head|header|hr|html|iframe|legend|li|link|main|menu|menuitem|nav|noframes|ol|optgroup|option|p|param|search|section|summary|table|tbody|td|tfoot|th|thead|title|tr|track|ul';
const HTML_BLOCK_6 = new RegExp(`^</?(?:${HTML_BLOCK_TAGS})(?=[\\s/>]|$)`, 'i');
const HTML_BLOCK_7 = /^(?:<[A-Za-z][A-Za-z0-9-]*(?:\s+[^<>]*)?\/?>|<\/[A-Za-z][A-Za-z0-9-]*\s*>)\s*$/;

/**
 * Every ATX heading that renders as a heading, in order:
 * {level, raw, text, offset, id}. `offset` is where its LINE starts in
 * `markdown`; `text` is plain; `id` is unique within the guide —
 * `user-content-<slug>`, a repeat gets `-2`, `-3`, and a heading whose
 * text slugs to nothing (non-ASCII only, or empty) gets
 * `user-content-section-<N>`, N its place among the headings.
 */
export function readGuideHeadings(markdown) {
  const src = String(markdown || '');
  const out = [];
  let offset = 0;
  let fence = null; // the opening fence run, while inside fenced code
  let html = null; // the end condition, while inside an HTML block
  let inParagraph = false;
  for (const line of linesOf(src)) {
    const start = offset;
    offset += line.length;
    const bare = line.replace(/\r?\n$/, '');
    const blank = bare.trim() === '';

    if (fence) {
      const f = bare.match(FENCE);
      if (f && f[1][0] === fence[0] && f[1].length >= fence.length && !bare.slice(f[0].length).trim()) {
        fence = null;
      }
      continue;
    }
    if (html) {
      if (html === 'blank') { if (blank) html = null; } else if (html.test(bare)) html = null;
      inParagraph = false;
      continue;
    }
    if (blank) { inParagraph = false; continue; }

    const f = bare.match(FENCE);
    if (f && !(f[1][0] === '`' && bare.slice(f[0].length).includes('`'))) {
      fence = f[1];
      inParagraph = false;
      continue;
    }

    const t = bare.replace(/^ {0,3}/, '');
    if (t.startsWith('<')) {
      let end = null;
      if (t.startsWith('<!--')) end = /-->/;
      else if (HTML_RAW_OPEN.test(t)) end = HTML_RAW_CLOSE;
      else if (t.startsWith('<?')) end = /\?>/;
      else if (t.startsWith('<![CDATA[')) end = /\]\]>/;
      else if (/^<![A-Za-z]/.test(t)) end = />/;
      else if (HTML_BLOCK_6.test(t)) end = 'blank';
      else if (!inParagraph && HTML_BLOCK_7.test(t)) end = 'blank';
      if (end) {
        // The end condition may be met on the opening line itself.
        const rest = t.startsWith('<!--') ? t.slice(4) : t;
        if (end !== 'blank' && end.test(rest)) { inParagraph = false; continue; }
        html = end;
        inParagraph = false;
        continue;
      }
    }

    const h = bare.match(ATX);
    if (h) {
      const raw = h[2].replace(/(^|[ \t])#+[ \t]*$/, '').trim();
      out.push({ level: h[1].length, raw, text: plainHeadingText(raw), offset: start });
      inParagraph = false;
      continue;
    }
    // An indented line cannot open a paragraph after a blank (it is code).
    if (!inParagraph && /^( {4}|\t)/.test(bare)) continue;
    inParagraph = true;
  }

  const used = new Set();
  out.forEach((heading, i) => {
    const slug = slugForHeading(heading.text);
    const base = slug ? `${HEADING_ID_PREFIX}${slug}` : `${HEADING_ID_PREFIX}section-${i + 1}`;
    let id = base;
    let n = 2;
    while (used.has(id)) { id = `${base}-${n}`; n += 1; }
    used.add(id);
    heading.id = id; // eslint-disable-line no-param-reassign
  });
  return out;
}

/** The Table of Contents: the guide's H2s, plain text, ids as rendered. */
export function generateGuideTOC(markdown) {
  return readGuideHeadings(markdown).filter((h) => h.level === 2 && h.text);
}

/** line-start offset -> id, for the heading renderers. */
export function headingIdsByLine(markdown) {
  return new Map(readGuideHeadings(markdown).map((h) => [h.offset, h.id]));
}

/**
 * The id of a heading rendered from `full` at `globalOffset` (any offset
 * on its line). A heading the reader does not list (a setext heading, one
 * inside a blockquote or a list) still gets an id, and one no slug can
 * produce — slugs never carry "_".
 */
export function headingIdAt(full, idsByLine, globalOffset) {
  const lineStart = String(full || '').lastIndexOf('\n', globalOffset - 1) + 1;
  return idsByLine.get(lineStart) || `${HEADING_ID_PREFIX}h_${globalOffset}`;
}

/**
 * { intro, sections, offsets } — split at the guide's H2 lines (the
 * reader's, so never inside code, a comment or an HTML block). Each
 * section begins with its "## " line; `offsets[i]` is where section i
 * starts in the input. `intro + sections.join('')` is exactly the input.
 */
export function splitGuideSections(markdown) {
  const src = String(markdown || '');
  const starts = readGuideHeadings(src).filter((h) => h.level === 2).map((h) => h.offset);
  if (!starts.length) return { intro: src, sections: [], offsets: [] };
  return {
    intro: src.slice(0, starts[0]),
    sections: starts.map((s, i) => src.slice(s, i + 1 < starts.length ? starts[i + 1] : src.length)),
    offsets: starts,
  };
}

/** Minimum number of H2 sections before a guide carries body photos. */
export const MIN_SECTIONS_FOR_PHOTOS = 3;

// A reference-style link / footnote definition: "[x]: url", "[^1]: note".
// A definition in one section and its use in another would break once the
// body is rendered as several blocks, so such a body is not split.
const REFERENCE_DEFINITION = /^ {0,3}\[[^\]\n]+\]:\s*\S/m;

/**
 * The photos the body may use, in order, never repeated.
 *
 * `gallery` is the care-guide payload's gallery ({display_url, url,
 * thumb_url, credit, credit_info}); `resolveUrl` turns a stored url into
 * one the browser can fetch (`buildAssetUrl`). When the sidebar shows the
 * gallery (`sidebarShowsGallery`), its first photo is the hero there and
 * is left out of the body; when a featured product card hides the
 * sidebar gallery, every photo is the body's to use.
 */
export function bodyPhotosFrom(gallery, { sidebarShowsGallery, resolveUrl }) {
  const list = Array.isArray(gallery) ? gallery : [];
  const valid = [];
  const seen = new Set();
  list.forEach((g, index) => {
    const src = resolveUrl(g?.display_url || g?.url);
    if (!src || seen.has(src)) return;
    seen.add(src);
    valid.push({
      src,
      number: index + 1,
      credit: (g?.credit || '').trim() || null,
      creditInfo: g?.credit_info || null,
    });
  });
  return sidebarShowsGallery ? valid.slice(1) : valid;
}

/**
 * The body as blocks to render, or null when the guide renders as one
 * block (as it always has): fewer than MIN_SECTIONS_FOR_PHOTOS H2
 * sections, no photo for the body, or a reference-style definition that
 * would not survive the split.
 *
 * Blocks: {kind: 'markdown', source, offset} | {kind: 'photo', photo}. A
 * photo follows every SECOND section (after 2, 4, …), never the last one,
 * and stops when the photos run out.
 */
export function planGuideBody(markdown, photos) {
  const list = Array.isArray(photos) ? photos : [];
  if (!markdown || list.length === 0) return null;
  if (REFERENCE_DEFINITION.test(markdown)) return null;
  const { intro, sections, offsets } = splitGuideSections(markdown);
  if (sections.length < MIN_SECTIONS_FOR_PHOTOS) return null;

  const blocks = [];
  if (intro.trim()) blocks.push({ kind: 'markdown', source: intro, offset: 0 });
  let next = 0;
  sections.forEach((source, i) => {
    blocks.push({ kind: 'markdown', source, offset: offsets[i] });
    const ordinal = i + 1;
    const isLast = ordinal === sections.length;
    if (ordinal % 2 === 0 && !isLast && next < list.length) {
      blocks.push({ kind: 'photo', photo: list[next] });
      next += 1;
    }
  });
  return blocks;
}
